import * as daHelper from '../../Dialogs/dialogActionsHelper';
import { dialogsNames } from '../../../enums/dialogsNames';
import * as shipmentsService from '../../../services/Shipments/shipmentsService';
import * as sensorProvidersService from '../../../services/SensorProviders/sensorProvidersService.js';
import * as locationsService from '../../../services/Locations/locationsService';
import Network from 'infrastructure/js/modules/network';
import { api as messageDialogApi } from '../../MessageDialog/messageDialogActions';
import { api as locationPageApi } from '../locationPageActions';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';

const config = {
  getSubmitMethod: () => shipmentsService.shipToCustomer,
};

export const actionTypes = {
  ...daHelper.getActionTypes(dialogsNames.SHIP_TO_CUSTOMER_DIALOG),
  SHIP_TO_CUSTOMER_DIALOG_FETCH_SENSORS_READY: 'SHIP_TO_CUSTOMER_DIALOG_FETCH_SENSORS_READY',
};

const actions = {
  ...daHelper.getActions(dialogsNames.SHIP_TO_CUSTOMER_DIALOG),
  fetchSensorsReady(payload) {
    return { type: actionTypes.SHIP_TO_CUSTOMER_DIALOG_FETCH_SENSORS_READY, payload };
  },
};

export let api = {
  ...daHelper.getApiActions(actions, config),

  fetchDialogData(dispatch, getState) {
    return function () {
      const promises = [
        locationsService.fetchLocationsByLocationType('OUTGOING_SHIPMENTS'),
        ...(PermissionManager.isTruckSensorsIntegrationEnabled() ? [sensorProvidersService.fetchSensorProviderConfigs({ businessId: '' })] : []),
      ];

      return Promise.all(promises).then((responses) => {
        const [locationsResponse, sensorProvidersResponse] = responses;
        if (!Network.isResponseValid(locationsResponse)) {
          console.error('Fetch Outgoing shipments locations failed', locationsResponse);
          messageDialogApi.responseError(dispatch, getState)(locationsResponse);
          return { success: false };
        }

        let dialogData = {
          locations:
            locationsResponse.dataList?.map((obj) => {
              return { value: obj.id, label: obj.businessId, data: obj };
            }) || [],
        };

        if (Network.isResponseValid(sensorProvidersResponse)) {
          dialogData.sensorProviders = getSensorProvidersOptions(sensorProvidersResponse?.dataList);
        }

        dispatch(actions.fetchDialogDataReady(dialogData));
        return { success: true };
      });
    };
  },

  getShipmentSensors(dispatch, getState) {
    return function (shipmentId, sensorProviderId, messageDialogBuilder) {
      dispatch(actions.setBusy(true));

      return shipmentsService
        .getExternalShipmentSensors(shipmentId, sensorProviderId)
        .then((response) => {
          if (!Network.isResponseValid(response)) {
            console.error('Create external shipment and fetch sensors failed', response);
            if (messageDialogBuilder) {
              const messageDialogDescriptor = getSensorIntegrationErrorMessageDescriptor(
                response,
                messageDialogBuilder,
                () => api.getShipmentSensors(dispatch, getState)(shipmentId, sensorProviderId, messageDialogBuilder),
                messageDialogApi.close(dispatch, getState)
              );

              messageDialogApi.open(dispatch, getState)(messageDialogDescriptor);
            }
            dispatch(actions.setBusy(false));
            return { success: false };
          }

          const sensors =
            response.dataList?.map((obj) => {
              return { value: obj.id, label: obj.name, data: obj };
            }) || [];

          dispatch(actions.fetchSensorsReady(sensors));
          return { success: true };
        })
        .catch((error) => {
          console.error('Create external shipment and fetch sensors failed', error);
          messageDialogApi.responseError(dispatch, getState)();
          dispatch(actions.setBusy(false));
          return { success: false };
        });
    };
  },

  assignSensorToShipment(dispatch, getState) {
    return function (data, messageDialogBuilder) {
      dispatch(actions.setBusy(true));
      const locationId = locationPageApi.getLocationId(dispatch, getState)();

      return shipmentsService
        .assignSensorToExternalShipment({ ...data, locationId })
        .then((response) => {
          if (!Network.isResponseValid(response)) {
            console.error('Assign sensor to shipment failed', response);
            if (messageDialogBuilder) {
              const messageDialogDescriptor = getSensorIntegrationErrorMessageDescriptor(
                response,
                messageDialogBuilder,
                () => api.assignSensorToShipment(dispatch, getState)(data, messageDialogBuilder),
                messageDialogApi.close(dispatch, getState)
              );

              messageDialogApi.open(dispatch, getState)(messageDialogDescriptor);
            }
            dispatch(actions.setBusy(false));
            return { success: false };
          }

          dispatch(actions.setBusy(false));
          return { success: true };
        })
        .catch((error) => {
          console.error('Assign sensor to shipment failed', error);
          messageDialogApi.responseError(dispatch, getState)();
          dispatch(actions.setBusy(false));
          return { success: false };
        });
    };
  },

  unAssignSensorFromShipment(dispatch, getState) {
    return function (shipmentId, sensorId, messageDialogBuilder) {
      dispatch(actions.setBusy(true));
      const locationId = locationPageApi.getLocationId(dispatch, getState)();

      return shipmentsService
        .unAssignSensorFromExternalShipment(shipmentId, sensorId, locationId)
        .then((response) => {
          if (!Network.isResponseValid(response)) {
            console.error('Unassign sensor to shipment failed', response);
            if (messageDialogBuilder) {
              const messageDialogDescriptor = getSensorIntegrationErrorMessageDescriptor(
                response,
                messageDialogBuilder,
                () => api.unAssignSensorFromShipment(dispatch, getState)(shipmentId, sensorId, messageDialogBuilder),
                messageDialogApi.close(dispatch, getState)
              );

              messageDialogApi.open(dispatch, getState)(messageDialogDescriptor);
            }
            dispatch(actions.setBusy(false));
            return { success: false };
          }

          dispatch(actions.setBusy(false));
          return { success: true };
        })
        .catch((error) => {
          console.error('Unassign sensor to shipment failed', error);
          messageDialogApi.responseError(dispatch, getState)();
          dispatch(actions.setBusy(false));
          return { success: false };
        });
    };
  },

  stopShipment(dispatch, getState) {
    return function (shipmentId, messageDialogBuilder) {
      dispatch(actions.setBusy(true));
      const locationId = locationPageApi.getLocationId(dispatch, getState)();

      return shipmentsService
        .stopExternalShipment(shipmentId, locationId)
        .then((response) => {
          if (!Network.isResponseValid(response)) {
            console.error('Stop shipment failed', response);
            dispatch(actions.setBusy(false));
            return { success: false };
          }

          dispatch(actions.setBusy(false));
          return { success: true };
        })
        .catch((error) => {
          console.error('Stop shipment failed', error);
          dispatch(actions.setBusy(false));
          return { success: false };
        });
    };
  },
};

export let jsxActions = {
  ...daHelper.getJsxActions(api),

  submit(data, messageDialogBuilder, reloadParentComponent) {
    return function (dispatch, getState) {
      return api.submit(dispatch, getState)(data, messageDialogBuilder, reloadParentComponent);
    };
  },

  getShipmentSensors(shipmentId, sensorProviderId, messageDialogBuilder) {
    return function (dispatch, getState) {
      return api.getShipmentSensors(dispatch, getState)(shipmentId, sensorProviderId, messageDialogBuilder);
    };
  },

  assignSensorToShipment(data, messageDialogBuilder) {
    return function (dispatch, getState) {
      return api.assignSensorToShipment(dispatch, getState)(data, messageDialogBuilder);
    };
  },

  unAssignSensorFromShipment(shipmentId, sensorId, messageDialogBuilder) {
    return function (dispatch, getState) {
      return api.unAssignSensorFromShipment(dispatch, getState)(shipmentId, sensorId, messageDialogBuilder);
    };
  },

  stopShipment(data, messageDialogBuilder) {
    return function (dispatch, getState) {
      return api.stopShipment(dispatch, getState)(data, messageDialogBuilder);
    };
  },
};

/////////////////////////////////////////////////////////////////////////
// PRIVATE HELPERS

function getSensorProvidersOptions(sensorProviders = []) {
  return sensorProviders.map((obj) => {
    return { value: obj.id, label: obj.businessId, data: obj };
  });
}

function getSensorIntegrationErrorMessageDescriptor(response, messageDialogBuilder, retryFunc, closeFunc) {
  const allLabels = createLabelHelper('');
  const sensorIntegrationErrorLabels = createLabelHelper('mat.locationpage.view.shipments.sensorIntegrationError.dialog.');
  const defaultMessage = sensorIntegrationErrorLabels.get('defaultMessage');

  const message = allLabels.get(
    response?.applicationResponseStatus?.errorCode,
    defaultMessage,
    response?.applicationResponseStatus?.errors?.mainError?.params
  );

  return messageDialogBuilder(message, closeFunc, retryFunc);
}